'use client';

import React from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Zap, Target, BarChart2, ArrowUpRight } from 'lucide-react';
import CtaLabel, { CTA_ASSESSMENT, CTA_BOOKING } from '@/components/ui/CtaLabel';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

const benefits = [
  {
    icon: Zap,
    code: 'INST-01',
    title: 'Instant Results',
    desc: 'Get your readiness score and service recommendations immediately after completing the assessment.',
  },
  {
    icon: Target,
    code: 'INST-02',
    title: 'Tailored to You',
    desc: 'Results are specific to your business stage, industry, and primary growth challenge.',
  },
  {
    icon: BarChart2,
    code: 'INST-03',
    title: 'Actionable Next Steps',
    desc: 'Receive a prioritised action plan with clear next steps matched to your business stage and goals.',
  },
];

export default function AssessmentCTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      data-ground
      id="assessment"
      className="relative overflow-hidden"
      ref={ref}
      style={{
        background: 'var(--m-band)',
        borderTop: '1px solid var(--m-band-edge)',
        borderBottom: '1px solid var(--m-band-edge)',
      }}
    >
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(var(--m-on-band-rgb),0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(var(--m-on-band-rgb),0.02) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Oversized background number */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none select-none hidden lg:block"
        style={{
          fontFamily: "'Archivo Narrow', sans-serif",
          fontSize: 'clamp(180px, 22vw, 320px)',
          fontWeight: 900,
          color: 'rgba(var(--m-on-band-rgb),0.025)',
          lineHeight: 1,
          letterSpacing: '-0.04em',
          userSelect: 'none',
          paddingRight: 40,
        }}
      >
        05
      </div>

      <div className="relative m-container section-rhythm">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24 items-start">
            {/* Left — headline + CTAs */}
            <div>
              {/* Section label */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  style={{ width: 24, height: 1, background: 'rgba(var(--m-on-band-rgb),0.2)' }}
                />
                <span
                  className="font-mono text-[11px] uppercase tracking-widest"
                  style={{ color: 'rgba(var(--m-on-band-rgb),0.35)', letterSpacing: '0.22em' }}
                >
                  Free Business Assessment
                </span>
              </div>

              <h2
                className={`${SECTION_HEADING_CLASS} mb-6`}
                style={{ ...SECTION_HEADING_STYLE, color: 'var(--m-on-band)' }}
              >
                Get a personalised
                <br />
                diagnosis in under
                <br />5 minutes
              </h2>

              {/* Thin accent line */}
              <div
                style={{
                  width: 48,
                  height: 2,
                  background: 'rgba(var(--m-on-band-rgb),0.3)',
                  marginBottom: 24,
                }}
              />

              <p
                className="leading-relaxed mb-8 max-w-md"
                style={{ color: 'rgba(var(--m-on-band-rgb),0.55)', lineHeight: 1.75, fontSize: 14 }}
              >
                Answer 8 questions about your business stage, goals, and current challenges.
                We&apos;ll generate a consultation readiness score, identify your primary growth
                blocker, and recommend the services most relevant to where you are right now.
              </p>

              {/* Progress indicator */}
              <div className="flex items-center gap-3 mb-8">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <div
                    key={n}
                    style={{
                      width: n <= 3 ? 20 : 8,
                      height: 2,
                      background:
                        n <= 3
                          ? 'rgba(var(--m-on-band-rgb),0.6)'
                          : 'rgba(var(--m-on-band-rgb),0.15)',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
                <span
                  className="font-mono text-[11px] uppercase tracking-widest ml-1"
                  style={{ color: 'rgba(var(--m-on-band-rgb),0.3)', letterSpacing: '0.14em' }}
                >
                  8 questions
                </span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/business-assessment"
                  className="inline-flex items-center justify-center gap-2 font-mono uppercase tracking-widest transition-all"
                  style={{
                    background: 'var(--m-on-band)',
                    color: 'var(--m-band)',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    padding: '14px 24px',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      'rgba(var(--m-on-band-rgb),0.88)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--m-on-band)';
                  }}
                >
                  <CtaLabel mobile={CTA_ASSESSMENT} desktop="Start the Assessment" />
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href="/book-a-consultation"
                  className="inline-flex items-center justify-center gap-2 font-mono uppercase tracking-widest transition-all"
                  style={{
                    border: '1px solid rgba(var(--m-on-band-rgb),0.18)',
                    color: 'rgba(var(--m-on-band-rgb),0.6)',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    padding: '14px 24px',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-on-band-rgb),0.45)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-on-band)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-on-band-rgb),0.18)';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(var(--m-on-band-rgb),0.6)';
                  }}
                >
                  <CtaLabel mobile={CTA_BOOKING} desktop="Skip to Booking" />
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Right — benefit cards */}
            <div className="space-y-px" style={{ background: 'rgba(var(--m-on-band-rgb),0.04)' }}>
              {benefits.map((item, i) => (
                <motion.div
                  key={`assess-feat-${item.title}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                  className="flex items-start gap-5 p-6 group transition-all duration-200"
                  style={{
                    background: 'rgba(var(--m-on-band-rgb),0.03)',
                    borderLeft: '2px solid transparent',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      'rgba(var(--m-on-band-rgb),0.06)';
                    (e.currentTarget as HTMLElement).style.borderLeftColor =
                      'rgba(var(--m-on-band-rgb),0.3)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      'rgba(var(--m-on-band-rgb),0.03)';
                    (e.currentTarget as HTMLElement).style.borderLeftColor = 'transparent';
                  }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 40,
                      height: 40,
                      border: '1px solid rgba(var(--m-on-band-rgb),0.1)',
                      background: 'rgba(var(--m-on-band-rgb),0.05)',
                    }}
                  >
                    <item.icon
                      className="w-4 h-4"
                      style={{ color: 'rgba(var(--m-on-band-rgb),0.6)' }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1.5">
                      <h3
                        className="font-bold uppercase"
                        style={{
                          fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                          fontSize: 17,
                          fontWeight: 600,
                          color: 'var(--m-on-band)',
                          letterSpacing: '0.04em',
                          lineHeight: 1.25,
                        }}
                      >
                        {item.title}
                      </h3>
                      <span
                        className="font-mono text-[8px] uppercase tracking-widest"
                        style={{ color: 'rgba(var(--m-on-band-rgb),0.2)', letterSpacing: '0.14em' }}
                      >
                        {item.code}
                      </span>
                    </div>
                    <p
                      className="leading-relaxed"
                      style={{
                        color: 'rgba(var(--m-on-band-rgb),0.72)',
                        lineHeight: 1.65,
                        fontSize: 15,
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Bottom stat strip */}
              <div
                className="flex items-center justify-between px-6 py-4"
                style={{
                  background: 'rgba(var(--m-on-band-rgb),0.05)',
                  borderTop: '1px solid rgba(var(--m-on-band-rgb),0.06)',
                }}
              >
                {[
                  { value: '< 5 min', label: 'Completion time' },
                  { value: '8', label: 'Questions' },
                  { value: '100%', label: 'Free' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p
                      className="font-bold uppercase"
                      style={{
                        fontFamily: "'Archivo Narrow', sans-serif",
                        fontSize: 18,
                        color: 'var(--m-on-band)',
                        letterSpacing: '0.02em',
                        lineHeight: 1,
                        marginBottom: 4,
                      }}
                    >
                      {stat.value}
                    </p>
                    <p
                      className="font-mono text-[8px] uppercase tracking-widest"
                      style={{ color: 'rgba(var(--m-on-band-rgb),0.3)', letterSpacing: '0.14em' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
